import api from './api';

/**
 * Consulta de operadora nas fontes públicas, e a regra de como o resultado
 * entra no formulário.
 *
 * A mesclagem mora aqui, fora do componente, porque é a parte com regra de
 * verdade — e regra de verdade precisa de teste. O componente só decide o que
 * mostrar.
 */

/** Campos que a consulta pode preencher. Nenhum outro é tocado. */
const CAMPOS_DA_CONSULTA = [
    'nome', 'razaoSocial', 'cnpj', 'registroAns',
    'telefone', 'email',
    'cep', 'logradouro', 'numero', 'complemento', 'bairro', 'nomeCidade', 'ufEstado',
    'modalidadeAns', 'situacaoAns',
];

/**
 * Campos que a consulta **nunca** altera, mesmo vazios.
 *
 * Pertencem à relação entre a clínica e o convênio — foram negociados, não
 * consultados. Nem a Receita nem a ANS têm o que dizer sobre eles, e sobrescrever
 * um percentual de repasse com dado de API seria corromper um acordo comercial.
 */
export const CAMPOS_NUNCA_AUTOMATICOS = [
    'prazoRepasseDias', 'percentualRepasse', 'numeroContrato',
    'codigoPrestador', 'observacoes', 'ativo',
];

/**
 * Campos em que sobrescrever é perda real de trabalho: o usuário digitou com
 * intenção, ou são dados que a clínica personalizou.
 *
 * Em cadastro novo, um campo vazio é preenchido à vontade. Preenchido, só muda
 * com confirmação. Em edição, nem com campo preenchido se mexe sem avisar.
 */
const CAMPOS_SENSIVEIS = ['nome', 'razaoSocial', 'telefone', 'email',
    'cep', 'logradouro', 'numero', 'complemento', 'bairro', 'nomeCidade', 'ufEstado'];

const vazio = (v) => v === null || v === undefined || String(v).trim() === '';

/**
 * Traduz a resposta da consulta para os nomes de campo do formulário.
 *
 * Os nomes não coincidem, e isso é proposital de cada lado: a API fala a língua
 * das fontes (`cidade`, `uf`, `nomeFantasia`), e o formulário a do cadastro
 * (`nomeCidade`, `ufEstado`, `nome`). A tradução fica num lugar só.
 *
 * `nome` recebe o nome fantasia, que é como a operadora é conhecida; sem ele,
 * cai na razão social, porque um cadastro sem nome não se salva.
 */
export function paraCamposDoFormulario(consulta) {
    if (!consulta) return null;
    return {
        ...consulta,
        nome: consulta.nomeFantasia || consulta.razaoSocial || null,
        nomeCidade: consulta.cidade ?? null,
        ufEstado: consulta.uf ?? null,
    };
}

/**
 * Decide o que entra no formulário a partir do resultado da consulta.
 *
 * @param formAtual     estado atual do formulário
 * @param consulta      resposta de `/planos-saude/consulta`
 * @param opcoes.edicao true quando se está editando um convênio já salvo
 * @returns `{ valores, preenchidos, conflitos }` — `valores` é o que aplicar,
 *          `preenchidos` os campos que mudaram, e `conflitos` os que a consulta
 *          traria diferente mas foram mantidos como estão
 */
export function mesclarConsulta(formAtual, consulta, { edicao = false } = {}) {
    const valores = {};
    const preenchidos = [];
    const conflitos = [];

    if (!consulta) return { valores, preenchidos, conflitos };

    // Divergência entre CNPJ e registro ANS: a consulta não é confiável como
    // fonte de preenchimento, porque não se sabe qual dos dois identifica a
    // operadora certa.
    if (consulta.divergenciaCnpj) return { valores, preenchidos, conflitos };

    for (const campo of CAMPOS_DA_CONSULTA) {
        const novo = consulta[campo];
        if (vazio(novo)) continue;

        const atual = formAtual?.[campo];

        if (vazio(atual)) {
            valores[campo] = novo;
            preenchidos.push(campo);
            continue;
        }

        // Comparação tolerante: o usuário pode ter digitado com máscara, acento
        // ou caixa diferente sem que o dado seja outro.
        if (equivalentes(atual, novo)) continue;

        // Campo sensível nunca é sobrescrito em silêncio, nos dois modos.
        //
        // Em edição, o CNPJ e o registro ANS entram na mesma proteção: trocá-los
        // num convênio já salvo é apontar o cadastro para outra operadora, com
        // guias e títulos já emitidos atrás dele. Modalidade e situação ANS, não:
        // atualizá-las é justamente o motivo de reconsultar.
        const sensivel = CAMPOS_SENSIVEIS.includes(campo);
        const identidade = campo === 'cnpj' || campo === 'registroAns';
        const protegido = sensivel || (edicao && identidade);

        if (protegido) {
            conflitos.push({ campo, atual, novo });
        } else {
            valores[campo] = novo;
            preenchidos.push(campo);
        }
    }

    return { valores, preenchidos, conflitos };
}

/** Dois valores dizem a mesma coisa? Ignora máscara, acento, caixa e espaço. */
function equivalentes(a, b) {
    return normalizarComparacao(a) === normalizarComparacao(b);
}

function normalizarComparacao(v) {
    return String(v ?? '')
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[^a-zA-Z0-9]/g, '')
        .toUpperCase();
}

/**
 * Chama a consulta no backend.
 *
 * O front nunca fala com a BrasilAPI ou a ANS: as URLs ficam no servidor, e o
 * que vai aqui são só os identificadores.
 *
 * `skipGlobalError` porque a tela mostra o erro no lugar certo, junto ao campo
 * consultado — o toast do interceptor apareceria por cima.
 */
export async function consultarOperadora({ cnpj, registroAns }) {
    const params = {};
    if (cnpj) params.cnpj = cnpj;
    if (registroAns) params.registroAns = registroAns;

    const { data } = await api.get('/planos-saude/consulta', { params, skipGlobalError: true });
    return data;
}
